import { useEffect, useRef, useState } from "react";
import { useRestaurant } from "../../context/restaurant-context";
import { EOrderType, IS3Object } from "../../graphql/customQueries";
import { GET_ORDER_DELIVERY_STATUS } from "../../graphql/customMutations";
import { getCloudFrontDomainName } from "../../private/aws-custom";
import { CachedImage } from "../../tabin/components/cachedImage";
import { isMobile } from "react-device-detect";
import { format, isToday } from "date-fns";
import { useLocation, useParams } from "react-router-dom";
import { Link } from "../../tabin/components/link";
import { useQuery } from "@apollo/client";

const POLL_INTERVAL_MS = 3 * 1000;
const POLL_TIMEOUT_MS = 30 * 1000;

export const OrderPlaced = () => {
    const { restaurant } = useRestaurant();
    const location = useLocation();

    const { orderType, tableNumber, orderScheduledAt, orderId } = location.state;

    const isDelivery = orderType === EOrderType.DELIVERY;

    const [deliveryTrackingUrl, setDeliveryTrackingUrl] = useState<string | null>(null);
    const [uberDirectDeliveryError, setDeliveryError] = useState<string | null>(null);
    const [pollingTimedOut, setPollingTimedOut] = useState(false);
    const pollStartTime = useRef(Date.now());

    const { data, stopPolling } = useQuery(GET_ORDER_DELIVERY_STATUS, {
        variables: { id: orderId },
        skip: !isDelivery || !orderId,
        pollInterval: POLL_INTERVAL_MS,
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        if (!isDelivery || !data) return;

        const order = data.getOrder;

        if (order?.uberDirectDeliveryError) {
            stopPolling();
            setDeliveryError(order.uberDirectDeliveryError);
            return;
        }

        if (order?.thirdPartyDeliveryId && order?.deliveryTrackingUrl) {
            stopPolling();
            setDeliveryTrackingUrl(order.deliveryTrackingUrl);
            return;
        }

        if (Date.now() - pollStartTime.current >= POLL_TIMEOUT_MS) {
            stopPolling();
            setPollingTimedOut(true);
        }
    }, [data]);

    const RestaurantLogo = (props: { image: IS3Object }) => {
        return (
            <CachedImage
                url={`${getCloudFrontDomainName()}/protected/${props.image.identityPoolId}/${props.image.key}`}
                className="mb-4"
                style={{ width: "150px" }}
                alt="restaurant-logo"
            />
        );
    };

    const DeliveryStatus = () => {
        if (deliveryTrackingUrl) {
            return (
                <>
                    <div className="h3 mt-3">Your food will be delivered to your selected address at the scheduled time.</div>
                    <div className="h2 mt-3">Track your order here:</div>
                    <Link
                        className="h2 mt-1"
                        onClick={() => {
                            if (deliveryTrackingUrl) window.open(deliveryTrackingUrl, "_blank");
                        }}
                    >
                        {deliveryTrackingUrl}
                    </Link>
                    <div className="mt-3">You can now close this page.</div>
                </>
            );
        }

        if (uberDirectDeliveryError) {
            return (
                <>
                    <div className="h3 mt-3" style={{ color: "red" }}>
                        There was an issue booking your delivery: {uberDirectDeliveryError}
                    </div>
                    <div className="mt-3">Please contact us for assistance.</div>
                </>
            );
        }

        if (pollingTimedOut) {
            return (
                <>
                    <div className="h3 mt-3" style={{ color: "red" }}>
                        We're having trouble confirming your delivery booking. Please call Tabin Support on{" "}
                        <a href="tel:0800006101">0800 006 101</a> for assistance.
                    </div>
                </>
            );
        }

        return (
            <>
                <div className="h3 mt-3">We're booking your Uber delivery driver now...</div>
                <div className="mt-2" style={{ color: "#888" }}>
                    This usually takes a few seconds.
                </div>
            </>
        );
    };

    return (
        <div style={{ padding: isMobile ? "24px" : "64px", textAlign: "center" }}>
            {restaurant && restaurant.logo && <RestaurantLogo image={restaurant.logo} />}
            {isDelivery ? (
                <>
                    <div className="h3">Your order has been placed!</div>
                    <DeliveryStatus />
                </>
            ) : tableNumber === null ? (
                <>
                    <div className="h3">Your order is confirmed!</div>
                    <div className="h3 mt-3">Prepare to collect your food from {restaurant && restaurant.name}.</div>
                    {!orderScheduledAt ? (
                        <div className="h2 mt-3">
                            Estimated Pickup Time:{" "}
                            {restaurant && restaurant.preparationTimeInMinutes ? `${restaurant.preparationTimeInMinutes} minutes` : "ASAP"}
                        </div>
                    ) : isToday(new Date(orderScheduledAt)) ? (
                        <div className="h2 mt-3">Estimated Pickup Time: Today, {format(new Date(orderScheduledAt), "h:mm a")}</div>
                    ) : (
                        <div className="h2 mt-3">Estimated Pickup Time: {format(new Date(orderScheduledAt), "do MMM yyyy, h:mm a")}</div>
                    )}
                    <div className="mt-3">You may now close this page.</div>
                </>
            ) : (
                <>
                    <div className="order-placed-success h3">Your order has been successfully forwarded to the kitchen!</div>
                    <div className="order-placed-table-message h3 mt-3">Your food will be served at Table {tableNumber}.</div>
                    <div className="order-placed-close-page mt-3">You may now close this page.</div>
                </>
            )}
        </div>
    );
};
